/**
 * FormsPage.tsx — Módulo Formularios. List Pattern — misma estructura visual
 * que ProjectsPage: TimeBandedList con checkboxes por fila, SelectionActionBar
 * a page root, acción "Nuevo formulario" como Pill en el TopBar (no en el
 * FilterRow). Ver DESIGN_SYSTEM.md → List Pattern.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate , useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { Recorrido } from '../components/ui/Recorrido.js';
import { useRecorrido } from '../hooks/useRecorrido.js';
import { FilterRow } from '../components/ui/FilterRow.js';
import { Checkbox } from '../components/ui/Checkbox.js';
import { SelectionActionBar } from '../components/ui/SelectionActionBar.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { PillGroup, Pill } from '../components/PillGroup.js';
import { TimeBandedList } from '../components/common/TimeBandedList.js';
import { useToast } from '../components/ui/Toast.js';
import { parseServerDate } from '../lib/date.js';
import { matchesSearch } from '../lib/searchTerms.js';
import { useForms, useUpdateForm, useDeleteForm, type FormListItem, useBulkSetFormsRead } from '../hooks/useForms.js';
import { CreateFormModal } from '../components/forms/CreateFormModal.js';
import { useConfirm } from '../components/ui/ConfirmDialog.js';

// Checkbox col idéntica a ProjectsPage/LeadsPage — mismo ritmo visual entre listas.
const GRID_COLS = 'var(--evo-table-check-col) minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)';

const COL_HEADER: React.CSSProperties = {
  padding: 'var(--evo-table-head-py) var(--evo-table-cell-px)',
  color: 'var(--evo-fg-3)',
  fontSize: 'var(--evo-list-header-fs)',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: 'var(--evo-ls-caps)',
  fontFamily: 'var(--evo-font)',
};

const COL_CELL: React.CSSProperties = {
  padding: 'var(--evo-table-cell-py) var(--evo-table-cell-px)', fontSize: 'var(--evo-list-subtitle-fs)', color: 'var(--evo-fg-3)',
  fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

function fmtDate(iso: string): string {
  return parseServerDate(iso).toLocaleDateString('es', { month: 'short', day: 'numeric', year: '2-digit' });
}

export function FormsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  // “+” de Relacionados (2026-08-29): ?nuevo=1&contactId&contactName abre el
  // flujo de crear con el cliente precargado y limpia los params.
  const [precargado, setPrecargado] = useState<{ id: number; name: string } | null>(null);
  useEffect(() => {
    if (searchParams.get('nuevo') !== '1') return;
    const cid = parseInt(searchParams.get('contactId') ?? '');
    const cname = searchParams.get('contactName') ?? '';
    if (Number.isFinite(cid)) setPrecargado({ id: cid, name: cname || `#${cid}` });
    setShowCreate(true);
    const next = new URLSearchParams(searchParams);
    next.delete('nuevo'); next.delete('contactId'); next.delete('contactName');
    setSearchParams(next, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [collapsedBands, setCollapsedBands] = useState<Set<string>>(new Set());

  const { data, isLoading } = useForms();
  const updateForm = useUpdateForm();
  const deleteForm = useDeleteForm();
  const setFormsRead = useBulkSetFormsRead();
  const { confirm, dialog: confirmDialog } = useConfirm();

  const forms = useMemo(() => {
    const all = data?.forms ?? [];
    // Por palabra y no por frase — ver lib/searchTerms.ts.
    return all.filter(f => matchesSearch(search, [f.name]));
  }, [data, search]);

  const { setOverride } = useTopBarCtx();
  useEffect(() => {
    setOverride({
      title: t('forms.list.pageTitle'),
      heroIcon: 'forms',
      heroIconColor: 'var(--evo-steel)',
      bottomActions: (
        <div data-tour-id="forms-nuevo" style={{ display: 'flex' }}>
          <PillGroup size="md">
            <Pill tone="cyan" icon="plus" onClick={() => setShowCreate(true)}>
              {t('forms.list.createBtn')}
            </Pill>
          </PillGroup>
        </div>
      ),
    });
    return () => setOverride({});
  }, [setOverride, t]);

  function toggleSelect(id: string) {
    setSelected(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  }

  async function handleBulkRead(read: boolean) {
    const formIds = Array.from(selected);
    try {
      const r = await setFormsRead.mutateAsync({ formIds, read });
      setSelected(new Set());
      toast.success(t(read ? 'forms.list.bulk.markedRead' : 'forms.list.bulk.markedUnread', { count: r.updated }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  }

  async function handleBulkEnable(enabled: boolean) {
    const ids = Array.from(selected);
    const results = await Promise.allSettled(ids.map(id => updateForm.mutateAsync({ id, enabled })));
    const okCount = results.filter(r => r.status === 'fulfilled').length;
    setSelected(new Set());
    toast.success(t(enabled ? 'forms.list.bulk.enabled' : 'forms.list.bulk.disabled', { count: okCount }));
  }

  async function handleBulkDelete() {
    const ids = Array.from(selected);
    // Los que tienen respuestas se preguntan aparte, con el total a la vista:
    // la confirmación de la barra no dice que se van a llevar datos de gente.
    const conRespuestas = (data?.forms ?? []).filter(f => selected.has(f.id) && f.submissionCount > 0);
    const total = conRespuestas.reduce((s, f) => s + f.submissionCount, 0);
    if (conRespuestas.length > 0) {
      const ok = await confirm({
        title: t('forms.list.bulk.withSubmissionsTitle', { forms: conRespuestas.length }),
        description: t('forms.list.bulk.withSubmissionsBody', { count: total }),
        confirmLabel: t('forms.list.bulk.deleteWithSubmissions'),
        tone: 'danger',
      });
      if (!ok) return;
    }
    const conIds = new Set(conRespuestas.map(f => f.id));
    const results = await Promise.allSettled(ids.map(id => deleteForm.mutateAsync({ id, withSubmissions: conIds.has(id) })));
    const okCount = results.filter(r => r.status === 'fulfilled').length;
    setSelected(new Set());
    if (okCount === ids.length) toast.success(t('forms.list.bulk.deleted', { count: okCount }));
    else toast.error(t('forms.list.bulk.deleteBlocked', { deleted: okCount, count: ids.length }));
  }

  // ANTES del return: un hook debajo de un return condicional cambia la
  // cantidad de hooks entre renders y React tira la pantalla entera.
  const recorrido = useRecorrido('forms', { hayDatos: forms.length > 0, listo: !isLoading });

  return (
    <div className="flex flex-col h-full animate-fade-blur-in">
      <Recorrido
        pasos={recorrido.pasos}
        abierto={recorrido.abierto}
        onCerrar={recorrido.cerrar}
        onTerminar={recorrido.terminar}
      />
      <FilterRow>
        <FilterRow.Search value={search} onChange={setSearch} placeholder={t('common.search', 'Buscar…')} />
      </FilterRow>

      {showCreate && (
        <CreateFormModal
          contact={precargado}
          onClose={() => { setShowCreate(false); setPrecargado(null); }}
          onCreated={id => { setShowCreate(false); navigate(`/formularios/${id}`); }}
        />
      )}

      <SelectionActionBar
        visible={selected.size > 0}
        countLabel={t('forms.list.selected', { count: selected.size })}
        onClearSelection={() => setSelected(new Set())}
      >
        {/* Leído / no leído alcanza a TODAS las respuestas de los formularios
            elegidos: marcar el formulario y dejar sus respuestas sin leer
            dejaría el contador prendido, que es lo que se quiso apagar. */}
        <SelectionActionBar.Action
          label={t('forms.list.bulk.markRead')}
          loading={setFormsRead.isPending}
          onClick={() => { void handleBulkRead(true); }}
        />
        <SelectionActionBar.Action
          label={t('forms.list.bulk.markUnread')}
          loading={setFormsRead.isPending}
          onClick={() => { void handleBulkRead(false); }}
        />
        <SelectionActionBar.Divider />
        <SelectionActionBar.Action label={t('forms.list.bulk.enableAction')} onClick={() => { void handleBulkEnable(true); }} />
        <SelectionActionBar.Action label={t('forms.list.bulk.disableAction')} tone="amber" onClick={() => { void handleBulkEnable(false); }} />
        <SelectionActionBar.Divider />
        <SelectionActionBar.Action label={t('common.delete')} tone="danger" confirmRequired onClick={() => { void handleBulkDelete(); }} />
      </SelectionActionBar>

      <div className="flex-1 overflow-hidden flex flex-col">

        {/* Column headers */}
        <div style={{
          display: 'grid', gridTemplateColumns: GRID_COLS, height: 'var(--evo-toolbar-height)',
          alignItems: 'center', borderBottom: '1px solid var(--evo-stroke)', flexShrink: 0, background: 'var(--evo-bg-2)',
        }}>
          <div data-tour-id="forms-seleccion" style={{ padding: 'var(--evo-table-head-py) var(--evo-list-checkbox-gap) var(--evo-table-head-py) var(--evo-list-pad-x)' }}>
            <Checkbox
              checked={forms.length > 0 && forms.every(f => selected.has(f.id))}
              indeterminate={selected.size > 0 && !forms.every(f => selected.has(f.id))}
              onChange={_ => {
                if (forms.every(f => selected.has(f.id))) {
                  forms.forEach(f => { if (selected.has(f.id)) toggleSelect(f.id); });
                } else {
                  forms.filter(f => !selected.has(f.id)).forEach(f => toggleSelect(f.id));
                }
              }}
            />
          </div>
          <div style={{ ...COL_HEADER, paddingLeft: 0 }}>{t('forms.list.columns.name')}</div>
          <div style={COL_HEADER}>{t('forms.list.columns.fields')}</div>
          <div style={COL_HEADER}>{t('forms.list.columns.submissions')}</div>
          <div data-tour-id="forms-col-pendientes" style={COL_HEADER}>{t('forms.list.columns.pending')}</div>
          <div data-tour-id="forms-col-estado" style={COL_HEADER}>{t('forms.list.columns.status')}</div>
          <div style={{ ...COL_HEADER, paddingRight: 'var(--evo-list-pad-x)', textAlign: 'right' }}>{t('projects.columns.created')}</div>
        </div>

        <div className="flex-1 overflow-auto" data-tour-id="forms-lista">
          {isLoading && <LoadingState size="sm" />}
          {!isLoading && forms.length === 0 && <EmptyState size="md" reserveSpace title={t('forms.list.empty')} />}
          {!isLoading && forms.length > 0 && (
            <TimeBandedList<FormListItem, string>
              items={forms}
              getTimestamp={f => f.createdAt}
              getId={f => f.id}
              checkedIds={selected}
              onBandCheck={(ids, allChecked) => {
                ids.forEach(id => {
                  const isSelected = selected.has(id);
                  if (allChecked && isSelected) toggleSelect(id);
                  else if (!allChecked && !isSelected) toggleSelect(id);
                });
              }}
              collapsedBands={collapsedBands}
              onToggleBand={band => setCollapsedBands(prev => {
                const next = new Set(prev);
                next.has(band) ? next.delete(band) : next.add(band);
                return next;
              })}
              renderItem={form => (
                <div
                  data-tour-id={form.id === forms[0]?.id ? 'forms-fila' : undefined}
                  style={{ display: 'flex', flexDirection: 'column' }}
                >
                  <div
                    data-list-item-id={form.id}
                    onClick={() => navigate(`/formularios/${form.id}`, {
                      state: { filteredIds: forms.map(f => f.id), filters: { search } },
                    })}
                    className="cursor-pointer"
                    style={{ display: 'grid', gridTemplateColumns: GRID_COLS, alignItems: 'flex-start', minHeight: 'var(--evo-list-row-min-height)' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--evo-list-row-hover-bg)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = ''; }}
                  >
                    <div
                      style={{ padding: 'calc(var(--evo-table-cell-py) + var(--evo-list-check-nudge)) var(--evo-list-checkbox-gap) var(--evo-table-cell-py) var(--evo-list-pad-x)' }}
                      onClick={e => { e.stopPropagation(); toggleSelect(form.id); }}
                    >
                      <div style={{ pointerEvents: 'none' }}>
                        <Checkbox checked={selected.has(form.id)} onChange={() => {}} />
                      </div>
                    </div>

                    <div style={{ padding: 'var(--evo-table-cell-py) var(--evo-table-cell-px) var(--evo-table-cell-py) 0', overflow: 'hidden' }}>
                      <div className="truncate" style={{ color: 'var(--evo-fg-1)', fontSize: 'var(--evo-list-title-fs)', fontWeight: 400, fontFamily: 'var(--evo-font)' }}>
                        {form.name}
                      </div>
                      {form.description && (
                        <div className="truncate" style={{ color: 'var(--evo-fg-3)', marginTop: '2px', fontSize: 'var(--evo-list-subtitle-fs)', fontFamily: 'var(--evo-font)' }}>
                          {form.description}
                        </div>
                      )}
                    </div>

                    <div style={COL_CELL}>{form.fieldCount}</div>
                    <div style={COL_CELL}>{form.submissionCount}</div>
                    <div style={COL_CELL}>
                      {form.pendingCount > 0 ? <Pill size="xs" tone="cyan">{form.pendingCount}</Pill> : '—'}
                    </div>
                    <div style={{ padding: 'var(--evo-table-cell-py) var(--evo-table-cell-px)', display: 'flex' }}>
                      <Pill size="xs" tone={form.enabled ? 'cyan' : 'muted'}>
                        {form.enabled ? t('forms.list.statusEnabled') : t('forms.list.statusDisabled')}
                      </Pill>
                    </div>
                    <div style={{ padding: 'var(--evo-table-cell-py) var(--evo-list-pad-x) var(--evo-table-cell-py) var(--evo-table-cell-px)', fontSize: 'var(--evo-list-subtitle-fs)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', textAlign: 'right' }}>
                      {fmtDate(form.createdAt)}
                    </div>
                  </div>
                  <div style={{ margin: '0 var(--evo-list-pad-x)', height: '1px', background: 'var(--evo-stroke)' }} />
                </div>
              )}
              emptyState={<EmptyState size="md" reserveSpace title={t('forms.list.empty')} />}
            />
          )}
        </div>
      </div>
      {confirmDialog}
    </div>
  );
}
